import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/shared/toast";
import { queryClient } from "@/shared/api/queryClient";
import { useJobStore } from "../../../store/jobStore";
import { retryJob } from "../api/jobStatusApi";
import { jobKeys } from "../api/jobQueries";
import { deleteReport } from "@/features/reports/api/reportApi";

/** Everything the prompt needs to act on a partial transcription failure. */
export interface TranscriptDecision {
  /** POST /jobs { jobId } continues generation; also keys status polling. */
  jobId: string;
  /** DELETE /report?reportId cancels; also what the registry resume keys on. */
  reportId: string;
  failedVideoIds: string[];
  /** Server-provided headline (falls back to default copy when absent). */
  message?: string;
}

interface Options {
  /** Run after either action succeeds (e.g. close the dialog). */
  onSettled?: () => void;
  /** Run after a successful cancel/delete — distinct from continue, which
   *  navigates to the generation surface. The generation page uses this to
   *  leave the now-deleted report's page. */
  onCancelled?: () => void;
}

/**
 * The continue / cancel decisions for a report parked in
 * AWAITING_TRANSCRIPT_DECISION (some videos failed transcription). Drives the
 * inline prompt on the generation surface.
 */
export function useTranscriptionFailureActions(
  prompt: TranscriptDecision | null,
  { onSettled, onCancelled }: Options = {},
) {
  const navigate = useNavigate();
  const toast = useToast();
  const markJobResumed = useJobStore((s) => s.markJobResumed);
  const removeJob = useJobStore((s) => s.removeJob);
  const [busy, setBusy] = useState(false);

  // Continue: the decision is implicit in the parked state, so POST /jobs
  // carries only the jobId. Then resume polling and watch progress.
  const onContinue = useCallback(async () => {
    if (!prompt || busy) return;
    setBusy(true);
    try {
      await retryJob(prompt.jobId);
      // Flip the (per-device) registry entry back to PENDING and reset its
      // status query so the poll predicate — which stopped at AWAITING_INPUT —
      // resumes off fresh server state. resetQueries also covers the case
      // where this device never tracked the job (no registry entry to resume).
      markJobResumed(prompt.jobId);
      void queryClient.resetQueries({ queryKey: jobKeys.status(prompt.jobId) });
      toast.success(
        "Continuing report generation. The untranscribed videos are still analyzed — only their audio is left out.",
      );
      onSettled?.();
      navigate(`/generating/${prompt.jobId}`);
    } catch (error) {
      toast.error(
        (error as { response?: { data?: { error?: string } } }).response?.data
          ?.error || "Couldn't continue the report. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }, [prompt, busy, markJobResumed, toast, onSettled, navigate]);

  // Cancel: AWAITING_INPUT is a deletable status (same rules as
  // PENDING_REVIEW), so the existing delete-report flow applies.
  const onCancel = useCallback(async () => {
    if (!prompt || busy) return;
    setBusy(true);
    try {
      await deleteReport(prompt.reportId);
      // Drop the registry entry — leaving the active set also nudges the My
      // Reports list to refetch.
      removeJob(prompt.jobId);
      toast.success("Report deleted.");
      onCancelled?.();
      onSettled?.();
    } catch (error) {
      toast.error(
        (error as { response?: { data?: { error?: string } } }).response?.data
          ?.error || "Couldn't delete the report. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }, [prompt, busy, removeJob, toast, onCancelled, onSettled]);

  return { onContinue, onCancel, busy };
}
